import extend from 'lodash/extend';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import INTENT_ATTRIBUTES from './intent-attributes';

/**
 * @param {!string|boolean} val
 * @return {boolean}
 */
const toBoolean = val => Boolean(val === true || val === 'true');

/**
 * @param {object} srcObj
 * @param {any} attrToFlat
 * @return {object}
 */
const flatten = (srcObj, attrToFlat) => {
  const objToFlat = srcObj[attrToFlat];
  if (objToFlat) {
    extend(srcObj, objToFlat);
    delete srcObj[attrToFlat];
  }
  return srcObj;
};

/**
 * @param {object} srcObj
 * @param {string} srcKey
 * @param {string} targetKey
 * @return {object}
 */
const extract = (srcObj, srcKey, targetKey) => {
  const newObj = {};
  const srcVal = srcObj[srcKey];

  if (srcVal) {
    targetKey = targetKey || srcKey;
    newObj[targetKey] = srcVal;
    delete srcObj[srcKey];
  }
  return newObj;
};

/**
 * @param {object} params
 * @return {object}
 */
const extractMerchantConfig = params => {
  const config = {};

  extend(config, extract(params, 'bannerURL'));
  extend(config, extract(params, 'billingConstraints'));
  extend(config, extract(params, 'buttonAnimationEnabled'));
  extend(config, extract(params, 'backgroundImageId'));
  extend(config, extract(params, 'buttonPosition'));
  extend(config, extract(params, 'billingCountries', 'billingConstraints'));
  extend(config, extract(params, 'clientId'));
  extend(config, extract(params, 'customerSupportUrl'));
  extend(config, extract(params, 'displayName'));
  extend(config, extract(params, 'defaultProfile'));
  extend(config, extract(params, 'dataLevel', 'datalevel'));
  extend(config, extract(params, 'externalClientId'));
  extend(config, extract(params, 'externalProfileId'));
  extend(config, extract(params, 'guestCheckout'));
  extend(config, extract(params, 'logo'));
  extend(config, extract(params, 'logoUrl'));
  extend(config, extract(params, 'merchantCountryCode'));
  extend(config, extract(params, 'newUserWelcomeMessage'));
  extend(config, extract(params, 'newUserWelcomeMessageDescription'));
  extend(config, extract(params, 'paymentConstraints'));
  extend(config, extract(params, 'payment', 'billingConstraints'));
  extend(config, extract(params, 'returningUserWelcomeMessage'));
  extend(config, extract(params, 'shipping', 'shippingConstraints'));
  extend(config, extract(params, 'shippingConstraints'));
  extend(config, extract(params, 'threeDSSetup'));
  extend(config, extract(params, 'tokenizationSetup'));
  extend(config, extract(params, 'widgetStyle'));
  extend(config, extract(params, 'websiteUrl'));
  extend(config, extract(params, 'srcDpaId'));

  return config;
};

/**
 * @param {object} merchantConfig
 */
const doThreeDSConfig = merchantConfig => {
  const threeDSSetup = merchantConfig.threeDSSetup;
  if (threeDSSetup && threeDSSetup.threeDSActive) {
    threeDSSetup.threeDSActive = toBoolean(threeDSSetup.threeDSActive);
  }
  if (threeDSSetup && threeDSSetup.threeDSSuppressChallenge) {
    threeDSSetup.threeDSSuppressChallenge = toBoolean(threeDSSetup.threeDSSuppressChallenge);
  }
};

/**
 * @param {object} merchantConfig
 */
const doTokenizationSetupConfig = merchantConfig => {
  const tokenizationSetup = merchantConfig.tokenizationSetup;
  if (tokenizationSetup && tokenizationSetup.enableTokenization !== undefined) {
    tokenizationSetup.enableTokenization = toBoolean(tokenizationSetup.enableTokenization);
  }
};

/**
 * map ['US', 'CA'] to [{countryCode: 'US'}, {countryCode: 'CA'}]
 * @param {array} regions
 * @return {object}
 */
const mapRegions = regions => {
  regions = filter(regions, region => Boolean(region));
  return map(regions, region => ({ countryCode: region }));
};

/**
 * @param {object} merchantConfig
 */
const doShippingConstraints = merchantConfig => {
  const shippingConstraints = merchantConfig.shippingConstraints;
  let acceptedRegions;

  if (shippingConstraints) {
    if (shippingConstraints.collectShipping === undefined) {
      // default true.
      shippingConstraints.collectShipping = true;
    } else {
      shippingConstraints.collectShipping = toBoolean(shippingConstraints.collectShipping);
    }
    acceptedRegions = shippingConstraints.acceptedRegions;
    if (acceptedRegions && typeof acceptedRegions[0] === 'string') {
      shippingConstraints.acceptedRegions = {};
      shippingConstraints.acceptedRegions.region = mapRegions(acceptedRegions);
    }
  }
};

/**
 * @param {object} merchantConfig
 */
const doBillingConstraints = merchantConfig => {
  let billingConstraints = merchantConfig.billingConstraints;
  const acceptedRegions = billingConstraints;

  if (billingConstraints) {
    billingConstraints = {};
    billingConstraints.acceptedRegions = {};
    billingConstraints.acceptedRegions.region = mapRegions(acceptedRegions);
    merchantConfig.billingConstraints = billingConstraints;
  }
};

/**
 * @param {object} merchantConfig
 * @param {object} params
 */
const doPaymentConstraints = (merchantConfig, params) => {
  let paymentConstraints = {};
  let cardBrands = params.cardBrands;
  let mappedCardBrands;

  if (cardBrands) {
    paymentConstraints = { paymentTypes: { paymentType: [] } };
    cardBrands = filter(cardBrands, cardBrand => Boolean(cardBrand));
    mappedCardBrands = map(cardBrands, cardBrand => ({ cardBrand }));
    paymentConstraints.paymentTypes.paymentType = mappedCardBrands;
    delete params.cardBrands;
  }

  extend(paymentConstraints, extract(params, 'acceptCanadianVisaDebit', 'acceptCanadianVISADebit'));

  if (!isEmpty(paymentConstraints)) {
    extend(paymentConstraints, {
      paymentCurrency: params.currencyCode
    });

    merchantConfig.paymentConstraints = paymentConstraints;
  }
};

/**
 * @param {!object} data
 * @return {object}
 */
const formatMerchantConfig = data => {
  data = extend({}, omit(data, INTENT_ATTRIBUTES.IGNORED_ATTRIBUTES));

  if (data.merchantConfig.externalClientId) {
    data.externalClientId = data.merchantConfig.externalClientId;
  }
  if (data.merchantConfig.referenceCallID || data.merchantConfig.referenceCallId) {
    data.referenceCallID =
      data.merchantConfig.referenceCallID || data.merchantConfig.referenceCallId;
  }
  //???? g.pick is not a function
  data.merchantConfig = omit(data.merchantConfig, INTENT_ATTRIBUTES.IGNORED_ATTRIBUTES);
  data.merchantConfig = pick(data.merchantConfig, INTENT_ATTRIBUTES.MERCHANT_CONFIG_WHITE_LIST);

  if (data.settings && data.settings.review) {
    data.buttonAction = data.settings.review.buttonAction;
    data.reviewMessage = data.settings.review.message;
  }

  data = pick(data, INTENT_ATTRIBUTES.SCHEMA_SURFACE);

  return data;
};

/**
 * @param {!object} data
 * @return {object}
 */
const prepareCreateIntent = data => {
  const intentData = formatMerchantConfig(data);
  extend(intentData, {
    channel: 'Web',
    eventSource: 'LightboxTW'
  });

  return intentData;
};

/**
 * @param {!object} paramObject
 * @return {object}
 */
const parseMerchantConfig = paramObject => {
  let parsed = paramObject;
  parsed = flatten(parsed, 'paymentRequest');
  parsed = flatten(parsed, 'payment');

  const merchantConfig = extractMerchantConfig(parsed);

  doThreeDSConfig(merchantConfig);
  doTokenizationSetupConfig(merchantConfig);
  doShippingConstraints(merchantConfig);
  doBillingConstraints(merchantConfig);
  doPaymentConstraints(merchantConfig, parsed);

  if (!isEmpty(merchantConfig)) {
    parsed.merchantConfig = merchantConfig;
  }

  delete parsed.clientId;

  return parsed;
};

export { formatMerchantConfig, parseMerchantConfig, prepareCreateIntent };
